import React, { useState, useEffect, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useResizeObserver } from 'usehooks-ts';
import { useSelector } from "react-redux";
import Select from 'react-select';

const StateWiseStatistics = () => {
  const pid = useSelector((state) => state.user.projectId);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [schemeTypeOptions, setSchemeTypeOptions] = useState([]);
  const [selectedSchemeTypes, setSelectedSchemeTypes] = useState([]);
  const containerRef = useRef(null);
  const { width: containerWidth } = useResizeObserver({ ref: containerRef });

  // Scheme type mapping with abbreviations and full forms
  const SCHEME_TYPE_MAPPING = {
    'sch': 'Schemes',
    'doc': 'Document',
    'dfl': 'DFL',
    'yc': 'Yojana Card'
    // Add more mappings as needed
  };

  const COLORS = {
    primary: 'url(#gradientPrimary)',
    secondary: 'url(#gradientSecondary)',
    tertiary: 'url(#gradientTertiary)'
  };

  const metrics = [
    { key: 'application_open', label: 'Application Open', color: COLORS.primary },
    { key: 'docket_submitted', label: 'Docket Submitted', color: COLORS.secondary },
    { key: 'br_count', label: 'BR Count', color: COLORS.tertiary },
  ];

  // Fetch scheme type options
  useEffect(() => {
    const fetchSchemeTypes = async () => {
      try {
        const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-scheme-type/?pid=${pid}`);
        const data = await response.json();
        
        // Map the scheme types to include both abbreviation and full form
        setSchemeTypeOptions(data.unique_Scheme_Types.map(type => {
          const fullForm = SCHEME_TYPE_MAPPING[type] || type;
          return { 
            value: type, 
            label: `${type} - ${fullForm}`,
            // Store both for easy access
            abbreviation: type,
            fullForm: fullForm
          };
        }));
      } catch (err) {
        console.error('Error fetching scheme types:', err);
      }
    };
    fetchSchemeTypes();
  }, [pid]);

  // Format selected values to show both abbreviation and full form
  const formatSelectedValues = (selected) => {
    return selected.map(item => ({
      ...item,
      label: `${item.abbreviation} - ${item.fullForm}`
    }));
  };

  // Fetch data with filters
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        let url = `https://api-project360-insights.haqdarshak.com/kpi/state-wise/?pid=${pid}`;
        
        // Add scheme type filter if any selected
        if (selectedSchemeTypes.length > 0) {
          selectedSchemeTypes.forEach(type => {
            url += `&SchemeType=${type.abbreviation}`;
          });
        }

        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch data');
        const result = await response.json();
        setData(result);
        setError(null);
      } catch (err) {
        setError(err.message);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [pid, selectedSchemeTypes]);

  const filteredData = data.filter(item => item.State);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const handlePrev = () => setCurrentPage(prev => Math.max(prev - 1, 0));
  const handleNext = () => setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));

  const numBars = paginatedData.length || 1;
  const barCategoryGap = numBars === 1 ? 
    300 : numBars === 2 ? 
    100 : numBars === 3 ?
    65 : numBars===4 ? 
    80: numBars===5 ?
    60: numBars===6 ? 
    40:20;

  const isSideBySide = numBars <= 3;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <strong>Error:</strong> {error}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow border-2 border-gray-300 max-w-[90vw] ml-[4vw] p-4 mb-10">
      <h2 className="text-2xl font-bold  text-pink-600 mb-6">
        State-wise Statistics
      </h2>

      {/* Scheme Type Filter */}
      

      {/* Controls */}
      <div className="grid grid-cols-5 gap-2">
        

        <div className="col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Filter by Service Type
        </label>
        <Select
          isMulti
          options={schemeTypeOptions}
          value={formatSelectedValues(selectedSchemeTypes)}
          onChange={setSelectedSchemeTypes}
          className="basic-multi-select"
          classNamePrefix="select"
          placeholder="Select service type..."
          isClearable
          isSearchable
          formatOptionLabel={(option) => (
            <div>
              <span className="text-gray-800 ml-1">{option.fullForm}</span>
            </div>
          )}
        />
      </div>

      <div className="ml-6">
          <div>
          <label htmlFor="items-per-page" className="block text-sm font-medium text-gray-700 mb-1">
            States Per Page
          </label>
          </div>
          <select
            id="items-per-page"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(0);
            }}
            className="px-8 py-1 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 20].map(num => (
              <option key={num} value={num}>{num}</option>
            ))}
          </select>
        </div>

        <div className="text-lg mt-4 ml-3 text-purple-700">
          Showing {paginatedData.length} of {filteredData.length} states
        </div>

        <div className="flex items-center space-x-2 mt-3">
          <button
            onClick={handlePrev}
            disabled={currentPage === 0}
            className={`px-4 py-2 rounded-md text-sm transition ${
              currentPage === 0
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            Previous
          </button>

          <span className="text-sm px-4 py-2 bg-gray-100 rounded">
            Page {currentPage + 1} of {totalPages || 1}
          </span>

          <button
            onClick={handleNext}
            disabled={currentPage >= totalPages - 1 || totalPages === 0}
            className={`px-4 py-2 rounded-md text-sm transition ${
              currentPage >= totalPages - 1 || totalPages === 0
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* Chart and Table */}
      <div className={`flex ${isSideBySide ? 'flex-col lg:flex-row' : 'flex-col'} gap-6`}>
        <div
          ref={containerRef}
          className={isSideBySide ? 'w-full lg:w-2/3' : 'w-full'}
          style={{ height: '400px' }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={paginatedData}
              margin={{ top: 30, right: 30, left:20 , bottom: 20 }}
              barCategoryGap={barCategoryGap}
              barGap={5}
            >
              <defs>
                <linearGradient id="gradientPrimary" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#4E71FF" stopOpacity={1} />
                  <stop offset="100%" stopColor="#889EFF" stopOpacity={1} />
                </linearGradient>
                <linearGradient id="gradientSecondary" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#FFC107" stopOpacity={1} />
                  <stop offset="100%" stopColor="#FFE082" stopOpacity={1} />
                </linearGradient>
                <linearGradient id="gradientTertiary" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#06923E" stopOpacity={1} />
                  <stop offset="100%" stopColor="#6EDC92" stopOpacity={1} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="State"
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fontSize: 18 }}
                interval={0}
                label={{value: 'State',position: 'insideBottom',
                    offset: -20,
                    fontSize: 14,
                    fontWeight: 'bold'}}
              />
              <YAxis tick={{ fontSize: 12 }} width={60} 
              label={{value: 'CASE Count',angle: -90,
                    position: 'insideLeft',
                    fontSize: 14,
                    fontWeight: 'bold',}} />
              <Tooltip
                contentStyle={{
                  background: '#ffffff',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              />
              <Legend
                layout="horizontal"
                verticalAlign="top"
                align="right"
                wrapperStyle={{ fontSize: '16px' }}
              />
              {metrics.map((metric) => (
                <Bar
                  key={metric.key}
                  dataKey={metric.key}
                  name={metric.label}
                  fill={metric.color}
                  radius={[6, 6, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Table */}
        <div className={isSideBySide ? 'w-full lg:w-1/3' : 'w-full'}>
          <div className="overflow-x-auto max-w-[calc(100vw-32px)] lg:max-w-full"> 
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-indigo-50 text-indigo-800 text-sm">
                  <th className="border px-4 py-3 text-left font-semibold">KPI</th>
                  {paginatedData.map((item, index) => (
                    <th key={index} className="border px-4 py-3 font-semibold">
                      {item.State}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {metrics.map((metric) => (
                  <tr key={metric.key} className="hover:bg-gray-50 text-sm">
                    <td className="border px-4 py-3 font-medium text-gray-700 bg-gray-50">
                      {metric.label}
                    </td>
                    {paginatedData.map((item, index) => (
                      <td key={index} className="border px-4 py-3 text-center">
                        {item[metric.key] || 0}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StateWiseStatistics;